import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Radio,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch,
  Grid,
  Checkbox,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, styled } from "@mui/material";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import WifiIcon from "@mui/icons-material/Wifi";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { jwtDecode } from "jwt-decode";

import {
  useGetcategoryQuery,
  useGetcategoryidQuery,
  useGetRcsTemplateViewQuery,
  useGetRcsTemplateListQuery,
  useGetrcsassociatebotidsQuery,
} from "../store/services/rcstemplateApi";
import {
  useCheckMpinStatusQuery,
  useVerifyMpinStatusMutation,
} from "../store/services/userApi";

// ─── Field styling (RCS-style) ────────────────────────────────────────────────
const fieldSx = {
  height: 35,
  borderRadius: "8px",
  background: "rgba(255,255,255,0.75)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7c3aed",
    borderWidth: "2px",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
    "& .MuiSelect-select": {
      padding: "0 14px",
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
      height: "100%",
      boxSizing: "border-box",
      minHeight: "unset",
    },
    "& .MuiSelect-icon": {
      right: 8,
      fontSize: "1.15rem",
    },
  },
};

const PhonePreview = styled(Card)(() => ({
  width: "260px",
  height: "440px",
  margin: "0 auto",
  border: "7px solid #1a1a1a",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  borderRadius: "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f8f9fa",
}));

// ─── Step label ───────────────────────────────────────────────────────────────
const StepBadge = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "50%",
      bgcolor: "#1976d2",
      color: "#fff",
      fontSize: "0.72rem",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

// ─── Template variable helpers (RCS uses [variable] syntax) ──────────────────
const extractTemplateVariables = (...texts) => {
  const regex = /\[\s*([^[]+?)\s*\]/g;
  const maxOccurrences = new Map();
  texts.filter(Boolean).forEach((text) => {
    const occurrencesInText = new Map();
    let match;
    while ((match = regex.exec(text)) !== null) {
      const name = match[1].trim();
      const occurrence = (occurrencesInText.get(name) || 0) + 1;
      occurrencesInText.set(name, occurrence);
      maxOccurrences.set(
        name,
        Math.max(maxOccurrences.get(name) || 0, occurrence),
      );
    }
  });
  return Array.from(maxOccurrences, ([name, count]) =>
    Array.from({ length: count }, (_, index) =>
      index === 0 ? name : `${name}__occurrence_${index + 1}`,
    ),
  ).flat();
};

const getTemplateVariableKey = (name, occurrence) =>
  occurrence === 1 ? name : `${name}__occurrence_${occurrence}`;

// Label = the text inside [ ] (e.g. "VAR1"); the box holds the value.
const formatTemplateVariableLabel = (key) =>
  key.replace(/__occurrence_\d+$/, "");

const buildInitialVariableValues = (keys) => {
  const obj = {};
  keys.forEach((k) => {
    obj[k] = "";
  });
  return obj;
};

const substituteVariables = (text, variables) => {
  if (!text) return text;
  const occurrences = new Map();
  return text.replace(/\[\s*([^[]+?)\s*\]/g, (placeholder, rawName) => {
    const name = rawName.trim();
    const occurrence = (occurrences.get(name) || 0) + 1;
    occurrences.set(name, occurrence);
    const value = variables?.[getTemplateVariableKey(name, occurrence)];
    return value === undefined || value === null || value === ""
      ? placeholder
      : value;
  });
};

const collectTemplateTexts = (mapped) => {
  if (!mapped) return [];
  const texts = [];
  if (mapped.textContent) texts.push(mapped.textContent);
  if (mapped.richCardStandAloneDetails) {
    texts.push(mapped.richCardStandAloneDetails.cardTitle);
    texts.push(mapped.richCardStandAloneDetails.cardDescription);
  }
  (mapped.richCardCarouselDetails?.cardsList || []).forEach((c) => {
    texts.push(c.cardTitle);
    texts.push(c.cardDescription);
  });
  return texts.filter(Boolean);
};

const normalizeRcsSuggestions = (...sources) => {
  const buttons = sources.find((source) => Array.isArray(source) && source.length) || [];
  return buttons
    .map((button, index) => ({
      suggestionId: button?.suggestionId ?? button?.suggestion_id ?? button?.button_no ?? index,
      suggestionText: String(
        typeof button === "string"
          ? button
          : button?.suggestion_text ?? button?.suggestionText ?? button?.text ?? button?.label ?? "",
      ),
    }))
    .filter((button) => button.suggestionText);
};

// Key = text inside [ ] (e.g. VAR1), value = what the user typed.
// Saved as a plain object: {"VAR1":"111","VAR2":"22"}
const toPayloadVariables = (vars) =>
  Object.fromEntries(
    Object.entries(vars).map(([key, value]) => [
      key.replace(/__occurrence_\d+$/, ""),
      value,
    ])
  );

// ─── Component ────────────────────────────────────────────────────────────────
const RcsConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const customerId = "1";

  const [campaignName, setCampaignName] = useState("");
  const [campaignCategory, setCampaignCategory] = useState("");
  const [selectedBotIds, setSelectedBotIds] = useState([]); // array of bot_id strings
  const [selectedRcsTemplateId, setSelectedRcsTemplateId] = useState(null); // internal rcs_template_id
  const [tokenCustomerId, setTokenCustomerId] = useState(null);
  const categoryTouchedRef = useRef(false);
  const [templateType, setTemplateType] = useState("rich card");
  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [message, setMessage] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [filterDatatill_days, setFilterDatatill_days] = useState(0);
  const [selectedBotTypeId, setSelectedBotTypeId] = useState(null);
  const [isHydrating, setIsHydrating] = useState(false);
  const [currentTime, setCurrentTime] = useState("11:50");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [carouselCards, setCarouselCards] = useState([]);
  const [ctaLabel, setCtaLabel] = useState(
    "From messages to meaningful journeys",
  );
  const [templateVariables, setTemplateVariables] = useState({});

  const nodeId = searchParams.get("nodeId");
  const { data: checkmpin } = useCheckMpinStatusQuery();
  const [verifyMpin] = useVerifyMpinStatusMutation();
  const { data: categoryResp, isLoading: isCategoryLoading } =
    useGetcategoryQuery();
  const botTypes = categoryResp?.data || [];

  useEffect(() => {
    if (!campaignCategory && botTypes.length > 0)
      setCampaignCategory(botTypes[0].bot_type_name);
  }, [botTypes, campaignCategory]);

  useEffect(() => {
    if (!campaignCategory || botTypes.length === 0) return;

    const found = botTypes.find((b) => b.bot_type_name === campaignCategory);
    setSelectedBotTypeId(found?.rcs_bot_type_id || null);

    if (isHydrating || isEditMode) return;

    setSelectedBotIds([]);
    setSelectedRcsTemplateId(null);
    setSelectedTemplateId(null);
    setTemplateName("");
    setMessage("");
    setMediaUrl("");
    setCarouselCards([]);
    setCtaLabel("From messages to meaningful journeys");
    setTemplateVariables({});
  }, [campaignCategory, botTypes, isHydrating, isEditMode]);

  const { data: botsResp, isLoading: isBotsLoading } = useGetcategoryidQuery(
    customerId,
    { skip: !customerId },
  );
  const botsList = botsResp?.data || [];

  let storedMeta = {};
  try {
    const raw = localStorage.getItem(`workflow-meta-${id}`);
    if (raw) storedMeta = JSON.parse(raw);
  } catch (e) {
    /* noop */
  }
  const workflowName = storedMeta?.name || "";

  useEffect(() => {
    if (workflowName) setCampaignName(workflowName);
  }, [workflowName]);

  useEffect(() => {
    if (!isEditMode) return;
    try {
      const stored = localStorage.getItem(`workflow-edit-graph-${id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        const rcsNode = parsed.nodes?.find((n) => n.id === nodeId);
        if (rcsNode?.data?.rcsConfig) {
          const c = rcsNode.data.rcsConfig;
          setIsHydrating(true);
          setCampaignName(c.campaignName);
          setCampaignCategory(c.campaignCategory || "");
          if (c.rcs_bot_type_id != null)
            setSelectedBotTypeId(c.rcs_bot_type_id);
          setSelectedBotIds(
            (Array.isArray(c.botIds) && c.botIds.length ? c.botIds : [c.agentId])
              .filter(Boolean)
              .map(String),
          );
          setSelectedRcsTemplateId(c.reference_rcs_template_id || null);
          setTemplateType(c.templateType || "rich card");
          setTemplateName(c.templateName || "");
          setMessage(c.message || "");
          setMediaUrl(c.mediaUrl || "");
          setFilterDatatill_days(c.filterDatatill_days ?? 0);
          setSelectedTemplateId(c.template_id || c.selectedTemplateId || null);

          const keys = extractTemplateVariables(c.message || "");
          setTemplateVariables({
            ...buildInitialVariableValues(keys),
            ...(c.template_variables && typeof c.template_variables === "object"
              ? c.template_variables
              : {}),
          });

          setTimeout(() => setIsHydrating(false), 0);
        }
      }
    } catch (err) {
      console.error(err);
      setIsHydrating(false);
    }
  }, [isEditMode, id, nodeId]);

  // ─── Agents (multi-select) ──────────────────────────────────────────────────
  const agentOptions = useMemo(() => {
    const filtered = selectedBotTypeId
      ? botsList.filter(
          (b) => Number(b.rcs_bot_type_id) === Number(selectedBotTypeId),
        )
      : botsList;
    return filtered.map((b) => ({
      id: b.bot_id,
      name: b.agent_name,
      botId: b.bot_id,
      rcsCustomerBotId: b.rcs_customer_bot_id,
    }));
  }, [botsList, selectedBotTypeId]);

  const allBotIds = useMemo(
    () => agentOptions.map((a) => String(a.id)),
    [agentOptions],
  );

  const selectedBots = useMemo(
    () => agentOptions.filter((a) => selectedBotIds.includes(String(a.id))),
    [agentOptions, selectedBotIds],
  );
  const botIdList = selectedBots.map((a) => String(a.id));
  const selectedInternalBotIds = useMemo(
    () =>
      [
        ...new Set(selectedBots.map((bot) => Number(bot.rcsCustomerBotId))),
      ].sort((a, b) => a - b),
    [selectedBots],
  );

  // primary agent = first selected (keeps save logic / disabled logic unchanged)
  const agentId = selectedBots[0]?.id ?? "";
  const agentName = selectedBots[0]?.name ?? "";

  // bots linked to the chosen template
  const { currentData: associatedBotsData, isSuccess: associatedBotsLoaded } =
    useGetrcsassociatebotidsQuery(
      { rcs_template_id: selectedRcsTemplateId },
      { skip: !selectedRcsTemplateId },
    );

  const associatedBotIds = useMemo(() => {
    const response = associatedBotsData?.data ?? associatedBotsData;
    const entries = Array.isArray(response)
      ? response
      : response?.bot_ids ??
        response?.botIds ??
        response?.associated_bot_ids ??
        response?.rcs_customer_bot_ids ??
        response?.associated_bots ??
        response?.bots;
    if (!Array.isArray(entries)) return null;
    return new Set(
      entries
        .flatMap((bot) =>
          typeof bot === "object" && bot !== null
            ? [bot.bot_id, bot.rcs_customer_bot_id, bot.id]
            : [bot],
        )
        .filter((x) => x !== null && x !== undefined)
        .map(String),
    );
  }, [associatedBotsData]);

  const isLinked = (a) =>
    !!associatedBotIds &&
    (associatedBotIds.has(String(a.id)) ||
      associatedBotIds.has(String(a.rcsCustomerBotId)));

  // before a template is picked: all bots; after: only the template's bots
  const selectableBotIds = useMemo(() => {
    if (!selectedRcsTemplateId || !associatedBotIds) return allBotIds;
    return agentOptions.filter(isLinked).map((a) => String(a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOptions, allBotIds, associatedBotIds, selectedRcsTemplateId]);

  const allSelected =
    selectableBotIds.length > 0 &&
    selectableBotIds.every((bid) => selectedBotIds.includes(bid));
  const someSelected = selectedBotIds.length > 0 && !allSelected;

  // 1) category / bot list changes -> select ALL agents, clear template
  useEffect(() => {
    if (isHydrating) return;
    if (isEditMode && !categoryTouchedRef.current) return; // keep saved bots in edit mode
    setSelectedBotIds(allBotIds);
    setSelectedTemplateId(null);
    setSelectedRcsTemplateId(null);
    setSelectedTemplate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBotIds]);

  // 2) template picked -> agents become the template's agents
  //    (user can still deselect/reselect afterwards)
  useEffect(() => {
    if (!selectedRcsTemplateId || !associatedBotsLoaded || !associatedBotIds)
      return;
    setSelectedBotIds(agentOptions.filter(isLinked).map((a) => String(a.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOptions, associatedBotIds, associatedBotsLoaded, selectedRcsTemplateId]);

  const handleBotChange = (e) => {
    let value = e.target.value;
    value = typeof value === "string" ? value.split(",") : value;
    const next = value.includes("__all__")
      ? allSelected
        ? []
        : selectableBotIds
      : value;
    setSelectedBotIds(
      allBotIds.filter(
        (bid) => next.includes(bid) && selectableBotIds.includes(bid),
      ),
    );
  };

  // ─── Templates (all templates of the customer) ──────────────────────────────
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;
      const d = jwtDecode(token);
      setTokenCustomerId(d?.customer_id || d?.id || d?.userid || null);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const { data: templatesResp, isLoading: isTemplatesLoading } =
    useGetRcsTemplateListQuery(
      { id: tokenCustomerId, page: 1, limit: 100 },
      { skip: !tokenCustomerId },
    );

  const rcsTemplates = useMemo(
    () =>
      (templatesResp?.data || []).map((t) => ({
        rcs_template_id: t.template_id || t.rcs_template_id || t.id,
        internal_template_id: t.rcs_template_id,
        template_name: t.template_name,
        rcs_template_type: t.rcs_template_type,
        status: t.status,
        __raw: t,
      })),
    [templatesResp],
  );

  // ⚠️ CHANGED: `currentData` instead of `data` so stale responses during
  // rapid template switching are ignored by RTK Query.
  const { currentData: templateViewResp } = useGetRcsTemplateViewQuery(
    selectedTemplateId,
    { skip: !selectedTemplateId },
  );

  // ⚠️ CHANGED: this effect now rebuilds `selectedTemplate` in full from the
  // view response (title, description, media, carousel cards, text, suggestions)
  // instead of only merging `suggestionsList` into the (mostly empty) object
  // that was created from the LIST payload in handleTemplateRowClick.
  useEffect(() => {
    if (!templateViewResp?.data) return;
    const { templateDetails, carousel_cards, buttonInformation } =
      templateViewResp.data;

    // Side state (unchanged behaviour, still needed for the save payload)
    if (templateDetails?.template_name)
      setTemplateName(templateDetails.template_name);
    if (templateDetails?.rcs_template_type)
      setTemplateType(templateDetails.rcs_template_type);
    setCarouselCards(carousel_cards || []);
    if (carousel_cards?.length > 0) {
      setMessage(carousel_cards[0].carousel_description || "");
      setMediaUrl(carousel_cards[0].carousel_image || "");
    } else {
      setMessage(
        templateDetails?.rich_card_description ||
          templateDetails?.rcs_text ||
          "",
      );
      setMediaUrl(templateDetails?.rich_card_image || "");
    }
    if (buttonInformation?.length > 0)
      setCtaLabel(
        buttonInformation[0].suggestion_text ||
          "From messages to meaningful journeys",
      );

    // Rebuild the preview object from the authoritative view response
    const type = normalizeType(templateDetails?.rcs_template_type);
    const suggestions = normalizeRcsSuggestions(
      buttonInformation,
      templateDetails?.buttonInformation,
      templateDetails?.buttons,
    );

    const mapped = {
      templateName: templateDetails?.template_name,
      templateType: type,
      textContent: templateDetails?.rcs_text || "",
      suggestionsList: type === "text" ? suggestions : [],
      richCardStandAloneDetails:
        type === "rich_card"
          ? {
              media: templateDetails?.rich_card_image
                ? { url: templateDetails.rich_card_image }
                : null,
              mediaHeight:
                templateDetails?.media_details === "Short"
                  ? "SHORT_HEIGHT"
                  : templateDetails?.media_details === "Medium"
                    ? "MEDIUM_HEIGHT"
                    : "TALL_HEIGHT",
              cardTitle: templateDetails?.rich_card_title,
              cardDescription: templateDetails?.rich_card_description,
              suggestionsList: suggestions,
            }
          : null,
      richCardCarouselDetails:
        type === "carousel"
          ? {
              cardsList: (carousel_cards || []).map((c) => ({
                cardMedia:
                  c.carousel_image || c.image
                    ? { url: c.carousel_image || c.image }
                    : null,
                cardTitle: c.carousel_title ?? c.title,
                cardDescription: c.carousel_description ?? c.description,
                suggestionsList: normalizeRcsSuggestions(
                  c.buttonInformation,
                  c.buttons,
                  suggestions,
                ),
              })),
            }
          : null,
    };

    setSelectedTemplate(mapped);

    // Re-derive variable keys from real content. Preserve any values the user
    // has already typed (e.g. during edit-mode hydration where template_variables
    // were seeded from the saved payload before this effect fires).
    const keys = extractTemplateVariables(...collectTemplateTexts(mapped));
    setTemplateVariables((prev) => {
      const next = buildInitialVariableValues(keys);
      Object.keys(next).forEach((k) => {
        if (prev[k] !== undefined && prev[k] !== "") next[k] = prev[k];
      });
      return next;
    });
  }, [templateViewResp]);

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.toLowerCase();
    return rcsTemplates
      .filter((t) => !t.status || String(t.status).toLowerCase() === "approved")
      .filter((t) => t.template_name?.toLowerCase().includes(q));
  }, [rcsTemplates, templateSearch]);

  useEffect(() => {
    const iv = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
      );
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const totalCards =
    selectedTemplate?.richCardCarouselDetails?.cardsList?.length || 0;
  const currentCard =
    selectedTemplate?.richCardCarouselDetails?.cardsList?.[carouselIndex] ||
    null;

  const normalizeType = (type) =>
    String(type || "")
      .toLowerCase()
      .replace(/\s+/g, "_");

  const mapTemplateForPreview = (t) => {
    const type = normalizeType(t.rcs_template_type);
    const suggestions = normalizeRcsSuggestions(
      t.buttonInformation,
      t.buttons,
    );
    return {
      templateName: t.template_name,
      templateType: type,
      textContent: t.rcs_text || "",
      suggestionsList: type === "text" ? suggestions : [],
      richCardStandAloneDetails:
        type === "rich_card"
          ? {
              media: t.rich_card_image ? { url: t.rich_card_image } : null,
              mediaHeight:
                t.media_details === "Short"
                  ? "SHORT_HEIGHT"
                  : t.media_details === "Medium"
                    ? "MEDIUM_HEIGHT"
                    : "TALL_HEIGHT",
              cardTitle: t.rich_card_title,
              cardDescription: t.rich_card_description,
              suggestionsList: suggestions,
            }
          : null,
      richCardCarouselDetails:
        type === "carousel"
          ? {
              cardsList: (t.carousel_cards || []).map((c) => ({
                cardMedia:
                  c.image || c.carousel_image
                    ? { url: c.image || c.carousel_image }
                    : null,
                cardTitle: c.title ?? c.carousel_title,
                cardDescription: c.description ?? c.carousel_description,
                suggestionsList: normalizeRcsSuggestions(
                  c.buttonInformation,
                  c.buttons,
                  t.buttonInformation,
                  t.buttons,
                ),
              })),
            }
          : null,
    };
  };

  const handleTemplateRowClick = (tpl) => {
    setSelectedTemplateId(tpl.rcs_template_id);
    setSelectedRcsTemplateId(tpl.internal_template_id);
    setTemplatePanelOpen(false);
    const t = tpl.__raw;
    const mapped = mapTemplateForPreview(t);
    // Placeholder from LIST payload (view response replaces it fully below).
    setSelectedTemplate(mapped);
    setCarouselIndex(0);
    setTemplateName(t.template_name || "");
    setTemplateType(t.rcs_template_type || "Rich Card");
    setMessage(t.rich_card_description || t.rcs_text || "");
    setMediaUrl(t.rich_card_image || "");
    setCtaLabel(
      t.buttons?.[0]?.suggestion_text || "From messages to meaningful journeys",
    );

    // ⚠️ CHANGED: reset variables so values don't leak between templates.
    // The view effect will rebuild the correct keys from the real content.
    setTemplateVariables({});
  };

  const handleClearTemplate = () => {
    setSelectedTemplateId(null);
    setSelectedRcsTemplateId(null);
    setSelectedTemplate(null);
    setCarouselIndex(0);
    setTemplateName("");
    setMessage("");
    setMediaUrl("");
    setTemplateType("rich card");
    setCarouselCards([]);
    setCtaLabel("From messages to meaningful journeys");
    setTemplateVariables({});
  };

  const handleVariableChange = (key, value) => {
    setTemplateVariables((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveToWorkflow = () => {
    try {
      const key = isEditMode
        ? `workflow-edit-graph-${id}`
        : `workflow-graph-${id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        localStorage.setItem(
          key,
          JSON.stringify({
            nodes: parsed.nodes.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      templateName,
                      rcsConfig: {
                        campaignName,
                        campaignCategory,
                        rcs_bot_type_id: selectedBotTypeId,
                        agentId,
                        agent_name: agentName,
                        botIds: botIdList,
                        selected_bot_ids: selectedInternalBotIds,
                        reference_rcs_template_id: Number(selectedRcsTemplateId),
                        templateType,
                        templateName,
                        message,
                        mediaUrl,
                        filterDatatill_days,
                        template_id: selectedTemplateId,
                        template_variables: toPayloadVariables(templateVariables),
                      },
                    },
                  }
                : n,
            ),
            edges: parsed.edges || [],
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
    navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`);
  };

  const variableKeys = Object.keys(templateVariables);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#f0f2f8",
        display: "flex",
        flexDirection: "column",
        fontSize: "0.8rem",
      }}
    >
      {/* ── Top bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 15, color: "black" }} />
        </IconButton>
        <ChatOutlinedIcon sx={{ fontSize: 18, color: "#4a63ff" }} />
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          RCS Campaign{" "}
          {isEditMode && (
            <span style={{ fontWeight: 400, color: "#888" }}>(Edit)</span>
          )}
        </Typography>
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          gap: 2,
          overflow: "hidden",
          alignItems: "flex-start",
        }}
      >
        {/* LEFT: form */}
        <Paper
          elevation={0}
          sx={{
            flex: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            overflowY: "auto",
          }}
        >
          {/* Step 1 */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}
            >
              <StepBadge n={1} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Create Campaign
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, mb: 1.2 }}>
              <TextField
                size="small"
                label="Campaign Name"
                value={campaignName}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={textFieldSx}
              />
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={campaignCategory}
                  label="Category"
                  onChange={(e) => {
                    categoryTouchedRef.current = true;
                    setCampaignCategory(e.target.value);
                  }}
                >
                  {isCategoryLoading && (
                    <MenuItem disabled>Loading…</MenuItem>
                  )}
                  {!isCategoryLoading && botTypes.length === 0 && (
                    <MenuItem disabled>No categories</MenuItem>
                  )}
                  {botTypes.map((b) => (
                    <MenuItem
                      key={b.rcs_bot_type_id}
                      value={b.bot_type_name}
                    >
                      {b.bot_type_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel>Select Agent</InputLabel>
                <Select
                  multiple
                  value={selectedBotIds}
                  onChange={handleBotChange}
                  label="Select Agent"
                  renderValue={(selected) => {
                    if (!selected.length) return "";
                    if (selected.length === allBotIds.length)
                      return `All agents (${selected.length})`;
                    if (selected.length === 1)
                      return (
                        agentOptions.find((a) => String(a.id) === selected[0])
                          ?.name || selected[0]
                      );
                    return `${selected.length} agents selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  {isBotsLoading && (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                  {!isBotsLoading && agentOptions.length === 0 && (
                    <MenuItem disabled>No agents found</MenuItem>
                  )}
                  {selectableBotIds.length > 1 && (
                    <MenuItem value="__all__" dense>
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                      />
                      <ListItemText
                        primary="Select all"
                        primaryTypographyProps={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      />
                    </MenuItem>
                  )}
                  {agentOptions.map((a) => {
                    const bid = String(a.id);
                    return (
                      <MenuItem
                        key={bid}
                        value={bid}
                        dense
                        disabled={!selectableBotIds.includes(bid)}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedBotIds.includes(bid)}
                        />
                        <ListItemText
                          primary={a.name}
                          primaryTypographyProps={{ fontSize: "0.8rem" }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Filter From LC */}
          {checkmpin?.data?.allow7_day_filter && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableFilterTill}
                    onChange={(e) => {
                      setEnableFilterTill(e.target.checked);
                      if (!e.target.checked) setFilterDatatill_days(0);
                    }}
                  />
                }
                label="Filter From LC"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
              {enableFilterTill && (
                <FormControl
                  size="small"
                  sx={{ mt: 0.8, width: 150, ...selectSx }}
                >
                  <InputLabel>Filter Till</InputLabel>
                  <Select
                    value={filterDatatill_days}
                    label="Filter Till"
                    onChange={(e) => setFilterDatatill_days(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 224 } } }}
                  >
                    <MenuItem value={1}>Today</MenuItem>
                    <MenuItem value={2}>Yesterday</MenuItem>
                    {Array.from({ length: 28 }, (_, i) => (
                      <MenuItem key={i + 3} value={i + 3}>
                        Last {i + 3} days
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />

          {/* Step 2 */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}
            >
              <StepBadge n={2} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Select Template
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Template Name"
                value={templateName}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={textFieldSx}
              />
              <Button
                variant="outlined"
                onClick={() => setTemplatePanelOpen(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.73rem",
                  px: 1.5,
                  py: 0,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  height: 35,
                  flexShrink: 0,
                }}
              >
                Choose
              </Button>
            </Box>

            <Typography
              sx={{ fontSize: "0.67rem", color: "text.secondary", mb: 0.6 }}
            >
              Use RCS rich templates with text &amp; images.
            </Typography>

            {selectedTemplateId && (
              <Box
                sx={{ mt: 0.6, display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  size="small"
                  onClick={handleClearTemplate}
                  sx={{ fontSize: "0.7rem", color: "error.main" }}
                >
                  Clear Template
                </Button>
              </Box>
            )}
          </Box>

          {/* Step 3: Template Variables — only shown when [var] placeholders exist */}
          {variableKeys.length > 0 && (
            <Box>
              <Divider sx={{ borderColor: "rgba(0,0,0,0.07)", mb: 1.5 }} />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <StepBadge n={3} />
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Template Variables
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.67rem",
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Fill values for the [variable] placeholders in the template.
              </Typography>

              <Grid container spacing={1.2}>
                {variableKeys.map((key) => (
                  <Grid item xs={6} key={key}>
                    <TextField
                      label={formatTemplateVariableLabel(key)}
                      value={templateVariables[key] ?? ""}
                      onChange={(e) =>
                        handleVariableChange(key, e.target.value)
                      }
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>

        {/* MIDDLE: phone preview */}
        <Paper
          elevation={0}
          sx={{
            flex: 1.2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexShrink: 0,
          }}
        >
          <PhonePreview>
            <Box
              sx={{
                height: 26,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#000",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "9px" }}>{currentTime}</Typography>
              <Box sx={{ display: "flex", gap: 0.4, alignItems: "center" }}>
                <WifiIcon sx={{ fontSize: 9 }} />
                <NetworkCellIcon sx={{ fontSize: 9 }} />
                <BatteryFullIcon sx={{ fontSize: 9 }} />
              </Box>
            </Box>

            <Box
              sx={{
                height: 44,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#fff",
                flexShrink: 0,
              }}
            >
              <IconButton size="small" sx={{ p: 0.4, mr: 0.8 }}>
                <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>
                Business Message
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                p: 1.5,
                bgcolor: "#e5ddd5",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minHeight: 0,
              }}
            >
              {selectedTemplate ? (
                <>
                  {selectedTemplate.templateType === "rich_card" && (
                    <Box sx={{ maxWidth: "92%" }}>
                      <Card
                        sx={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #e0e0e0",
                          bgcolor: "#fff",
                        }}
                      >
                        {selectedTemplate.richCardStandAloneDetails?.media
                          ?.url && (
                          <Box
                            sx={{
                              height:
                                selectedTemplate.richCardStandAloneDetails
                                  .mediaHeight === "SHORT_HEIGHT"
                                  ? 70
                                  : selectedTemplate.richCardStandAloneDetails
                                        .mediaHeight === "MEDIUM_HEIGHT"
                                    ? 110
                                    : 140,
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={
                                selectedTemplate.richCardStandAloneDetails.media
                                  .url
                              }
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        )}
                        <Box sx={{ p: 1.2 }}>
                          {selectedTemplate.richCardStandAloneDetails
                            ?.cardTitle && (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                mb: 0.3,
                              }}
                            >
                              {substituteVariables(
                                selectedTemplate.richCardStandAloneDetails
                                  .cardTitle,
                                templateVariables,
                              )}
                            </Typography>
                          )}
                          {selectedTemplate.richCardStandAloneDetails
                            ?.cardDescription && (
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.secondary",
                                lineHeight: 1.4,
                                mb: 0.8,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {substituteVariables(
                                selectedTemplate.richCardStandAloneDetails
                                  .cardDescription,
                                templateVariables,
                              )}
                            </Typography>
                          )}
                          {selectedTemplate.richCardStandAloneDetails
                            ?.suggestionsList?.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {selectedTemplate.richCardStandAloneDetails.suggestionsList.map(
                                (s, i) => (
                                  <Button
                                    key={i}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      fontSize: "10px",
                                      p: "3px 7px",
                                      minWidth: 0,
                                      borderRadius: "5px",
                                    }}
                                  >
                                    {s.suggestionText}
                                  </Button>
                                ),
                              )}
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Box>
                  )}

                  {selectedTemplate.templateType === "carousel" &&
                    currentCard && (
                      <Card
                        sx={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #e0e0e0",
                          bgcolor: "#fff",
                          p: 1,
                        }}
                      >
                        {totalCards > 1 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.8,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                setCarouselIndex(
                                  (i) => (i - 1 + totalCards) % totalCards,
                                )
                              }
                              sx={{ p: 0.4, bgcolor: "rgba(0,0,0,0.08)" }}
                            >
                              <ChevronLeftIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Typography
                              sx={{ fontSize: "10px", color: "text.secondary" }}
                            >
                              Card {carouselIndex + 1} / {totalCards}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setCarouselIndex((i) => (i + 1) % totalCards)
                              }
                              sx={{ p: 0.4, bgcolor: "rgba(0,0,0,0.08)" }}
                            >
                              <ChevronRightIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                        {currentCard.cardMedia?.url && (
                          <Box
                            sx={{
                              height: 120,
                              overflow: "hidden",
                              borderRadius: "6px",
                              mb: 0.8,
                            }}
                          >
                            <img
                              src={currentCard.cardMedia.url}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        )}
                        {currentCard.cardTitle && (
                          <Typography
                            sx={{ fontSize: "12px", fontWeight: 700, mb: 0.3 }}
                          >
                            {substituteVariables(
                              currentCard.cardTitle,
                              templateVariables,
                            )}
                          </Typography>
                        )}
                        {currentCard.cardDescription && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "text.secondary",
                              lineHeight: 1.4,
                              mb: 0.8,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {substituteVariables(
                              currentCard.cardDescription,
                              templateVariables,
                            )}
                          </Typography>
                        )}
                        {currentCard.suggestionsList?.length > 0 && (
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {currentCard.suggestionsList.map((s, i) => (
                              <Button
                                key={i}
                                variant="outlined"
                                size="small"
                                sx={{
                                  fontSize: "10px",
                                  p: "3px 7px",
                                  minWidth: 0,
                                  borderRadius: "5px",
                                }}
                              >
                                {s.suggestionText}
                              </Button>
                            ))}
                          </Box>
                        )}
                        {totalCards > 1 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              gap: 0.4,
                              mt: 1,
                            }}
                          >
                            {Array.from({ length: totalCards }).map((_, i) => (
                              <Box
                                key={i}
                                onClick={() => setCarouselIndex(i)}
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  bgcolor:
                                    i === carouselIndex ? "#2196f3" : "#ccc",
                                  cursor: "pointer",
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Card>
                    )}

                  {selectedTemplate.templateType === "text" && (
                    <Box
                      sx={{
                        alignSelf: "flex-end",
                        maxWidth: "82%",
                        ml: "auto",
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.2,
                          bgcolor: "#dcf8c6",
                          borderRadius: "10px 10px 0 10px",
                          border: "1px solid #c5e1a5",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            whiteSpace: "pre-line",
                            lineHeight: 1.4,
                          }}
                        >
                          {substituteVariables(
                            selectedTemplate.textContent,
                            templateVariables,
                          ) || "Text content"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "text.secondary",
                  }}
                >
                  <Typography sx={{ fontSize: "11px" }}>
                    Select a template
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>to preview</Typography>
                </Box>
              )}
            </Box>
          </PhonePreview>
        </Paper>

        {/* RIGHT: template panel */}
        {templatePanelOpen && (
          <Paper
            elevation={0}
            sx={{
              width: 300,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(8px)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.8,
                py: 1,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                RCS Templates
              </Typography>
              <IconButton
                size="small"
                onClick={() => setTemplatePanelOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 15, color: "red" }} />
              </IconButton>
            </Box>

            <Box sx={{ px: 1.5, py: 0.8, flexShrink: 0 }}>
              <TextField
                size="small"
                placeholder="Search templates…"
                fullWidth
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                InputProps={{
                  sx: {
                    fontSize: "0.75rem",
                    height: 32,
                    bgcolor: "rgba(255,255,255,0.6)",
                  },
                }}
              />
            </Box>

            <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {isTemplatesLoading ? (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  Loading…
                </Typography>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      />
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Type
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.map((tpl) => (
                      <TableRow
                        key={tpl.rcs_template_id}
                        hover
                        onClick={() => handleTemplateRowClick(tpl)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ py: 0.4, pl: 1 }}>
                          <Radio
                            size="small"
                            checked={selectedTemplateId === tpl.rcs_template_id}
                            sx={{ p: 0 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.73rem", py: 0.5 }}>
                          {tpl.template_name}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.7rem",
                            py: 0.5,
                            color: "text.secondary",
                          }}
                        >
                          {tpl.rcs_template_type}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!isTemplatesLoading && filteredTemplates.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography
                            sx={{
                              fontSize: "0.73rem",
                              color: "text.secondary",
                              textAlign: "center",
                              py: 1,
                            }}
                          >
                            No templates found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          disabled={
            !campaignName ||
            !campaignCategory ||
            selectedBotIds.length === 0 ||
            !templateType ||
            !templateName ||
            !selectedTemplateId
          }
          onClick={handleSaveToWorkflow}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default RcsConfig;